/**
 * @module app.shared
 */

/**
 * JavaScript uses the `valueOf` function to implement the comparison operators, such as `<`
 */
export interface IComparable {
    valueOf(): unknown;
}

/**
 * Function that compares two items and returns a negative number if the first is less than
 * the second, a positive number if the first is greater than the second, or zero if they
 * are equal.
 */
export type Comparer<T> = (a: T, b: T) => number;

/**
 * Function that takes an item in an array and returns a value based on that item
 */
export type Selector<T, R> = (source: T) => R;

/** no-op comparison */
const alwaysEqualComparer: Comparer<any> = (a, b) => 0;

/**
 * Compare two values using the `<` and `>` operators, accounting for `undefined` and `null`
 * @param a the first value
 * @param b the second value
 * @returns `0`, `-1`, or `1` depending on how `a` compares to `b`
 */
export function compare(a: IComparable, b: IComparable): number {
    if(a === undefined || a === null) {
        if(b === undefined || b === null) { return 0; }
        return -1;
    }
    if(b === undefined || b === null) { return 1; }
    if(a < b) { return -1; }
    if(a > b) { return 1; }
    return 0;
}

/**
 * Return a comparer that falls back on multiple comparers when the items are "equal"
 * @param comparers array of one or more comparers
 * @returns a single comparer
 */
export function chainComparer<T>(comparers: Comparer<T>[]): Comparer<T> {
    if(comparers.length === 0) { return alwaysEqualComparer; }
    if(comparers.length === 1) { return comparers[0]; }
    return (a, b) => {
        for(const comparer of comparers) {
            const result = comparer(a, b);
            if(result !== 0) { return result; }
        }
        return 0;
    }
}

/**
 * Return a comparer for sorting selected values in ascending order
 * @param selector a function that takes a value in the array and returns the sort criteria
 * @returns a comparer function
 */
export function asc<T, R>(selector: Selector<T, R>): Comparer<T> {
    return ((a, b) => compare(selector(a), selector(b)));
}

/**
 * Return a comparer for sorting selected values in descending order
 * @param selector a function that takes a value in the array and returns the sort criteria
 * @returns a comparer function
 */
export function desc<T, R>(selector: Selector<T, R>): Comparer<T> {
    return ((a, b) => -compare(selector(a), selector(b)));
}

/**
 * Sort an array using one or more comparers
 *
 * @example
 * ```ts
 * // sort employees by:
 * // - Last Name (ascending)
 * // - First Name (ascending)
 * // - Hire Date (descending)
 * sortArray(employees,
 *     asc(e => e.lastName),
 *     asc(e => e.firstName),
 *     desc(e => e.hireDate))
 * ```
 * @param array the array to sort
 * @param comparers one or more comparers
 * @returns the passed in `array` after it is mutated in place
 */
export function sortArray<T>(array: T[], ...comparers: Comparer<T>[]): T[] {
    return array.sort(chainComparer(comparers));
}

/**
 * Ensure the given argument is actually an array.
 *
 * Useful for data that may have come from an unreliable source, such as parsed JSON.
 * @param array a value that *might* be array
 * @returns the original `array` value, or an empty array otherwise
 */
export function ensureArray<T>(array?: T[] | null): T[] {
    return Array.isArray(array) ? array : [];
}

/**
 * JavaScript's default .sort() method does lexicographical sorting (so "10" before "2").
 * Here is a natural sort algo that can handle mixed alphanumeric strings in a human-intuitive way.
 * Example: ["1", "10", "2", "A1", "A10", "A2"] -> ["1", "2", "10", "A1", "A2", "A10"]
 *
 * @param array a value that *might* be array
 * @param key if array is of objects, specify which property to sort on
 * @returns an empty array if input is not an array, or the same array, sorted
 */
export function naturalSort<T>(array: T[], key?: keyof T): T[] {
    if(!Array.isArray(array)) return [];
    return array.sort((a, b) => {
        const aVal = key ? String(a[key]) : String(a);
        const bVal = key ? String(b[key]) : String(b);

        const regex = /(\d+)|(\D+)/g;
        const aParts = aVal.match(regex) || [];
        const bParts = bVal.match(regex) || [];

        const len = Math.max(aParts.length, bParts.length);
        for(let i = 0; i < len; i++) {
            const aPart = aParts[i];
            const bPart = bParts[i];

            if(aPart === undefined) return -1;
            if(bPart === undefined) return 1;

            const aNum = Number(aPart);
            const bNum = Number(bPart);

            const bothNumeric = !isNaN(aNum) && !isNaN(bNum);

            if(bothNumeric) {
                if(aNum !== bNum) return aNum - bNum;
            } else {
                if(aPart !== bPart) return aPart.localeCompare(bPart);
            }
        }
        return 0;
    })

}
